/**
 * Clipboard and drop handling for media files (TASK-405).
 *
 * Kept as plain functions over `DataTransfer` so the decision — "which of these
 * items is a file we should upload" — is testable without a DOM, a browser or
 * a ProseMirror instance.
 *
 * The editor only knows "upload this file"; where it ends up is the caller's
 * business (AGENTS.md §13).
 */

/**
 * Image types the upload API accepts (SPEC §18).
 *
 * Mirrors the server's allow-list. A file rejected here never becomes a request
 * that fails at the network layer, which is a better experience and one less
 * round trip.
 */
export const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

/**
 * Attachment types the upload API accepts.
 *
 * Mirrors the server's attachment allow-list. SVG is off on both sides: it is
 * an executable document, not an image format a CMS should auto-insert.
 */
export const ACCEPTED_ATTACHMENT_TYPES = [
  "application/pdf",
  "application/zip",
  "text/plain",
  "text/csv",
  "audio/mpeg",
  "video/mp4",
] as const;

/** True when `type` is an accepted image type. */
export function isAcceptedImageType(type: string): boolean {
  return (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type.toLowerCase());
}

/** True when `type` is an accepted non-image attachment type. */
export function isAcceptedAttachmentType(type: string): boolean {
  return (ACCEPTED_ATTACHMENT_TYPES as readonly string[]).includes(type.toLowerCase());
}

/** True when `type` is accepted as an upload of either kind. */
export function isAcceptedMediaType(type: string): boolean {
  return isAcceptedImageType(type) || isAcceptedAttachmentType(type);
}

/** The subset of `DataTransfer` these helpers read. */
export interface TransferLike {
  readonly files?: ArrayLike<File> | undefined;
  readonly items?: ArrayLike<TransferItemLike> | undefined;
}

/** The subset of `DataTransferItem` these helpers read. */
export interface TransferItemLike {
  readonly kind?: string | undefined;
  readonly type?: string | undefined;
  getAsFile?: (() => File | null) | undefined;
}

/**
 * The image files carried by a paste or a drop.
 *
 * `files` is what a drop provides and `items` is what a paste provides, so both
 * are read. The same file can appear in both, which is why the result is
 * de-duplicated — uploading one paste twice would be a confusing bug.
 */
export function imageFilesFrom(transfer: TransferLike | null | undefined): File[] {
  return mediaFilesFrom(transfer, isAcceptedImageType);
}

/**
 * The non-image attachment files carried by a paste or a drop.
 *
 * A PDF dragged onto the editor is an attachment, not a broken image. Disjoint
 * from `imageFilesFrom` by construction, so a file never uploads twice.
 */
export function attachmentFilesFrom(transfer: TransferLike | null | undefined): File[] {
  return mediaFilesFrom(transfer, isAcceptedAttachmentType);
}

/** Shared walk over a `DataTransfer`, filtered by an accepted-type predicate. */
function mediaFilesFrom(
  transfer: TransferLike | null | undefined,
  accepted: (type: string) => boolean,
): File[] {
  if (transfer === null || transfer === undefined) {
    return [];
  }

  const found: File[] = [];
  const seen = new Set<string>();

  const add = (file: File | null | undefined): void => {
    if (file === null || file === undefined) {
      return;
    }

    if (!accepted(file.type)) {
      return;
    }

    // Name plus size plus type is enough to spot the duplicate; hashing the
    // bytes here would mean reading them twice.
    const key = `${file.name}:${String(file.size)}:${file.type}`;

    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    found.push(file);
  };

  for (let index = 0; index < (transfer.files?.length ?? 0); index += 1) {
    add(transfer.files?.[index]);
  }

  for (let index = 0; index < (transfer.items?.length ?? 0); index += 1) {
    const item = transfer.items?.[index];

    if (item?.kind !== "file" || item.getAsFile === undefined) {
      continue;
    }

    add(item.getAsFile());
  }

  return found;
}

/** True when a paste or drop carries at least one acceptable image. */
export function hasImage(transfer: TransferLike | null | undefined): boolean {
  return imageFilesFrom(transfer).length > 0;
}

/**
 * Escape text for a Markdown image alt attribute.
 *
 * Alt text is author-supplied (a filename, usually), and an unescaped `]` or
 * newline would break the surrounding `![...]()` construct.
 */
export function toAltText(filename: string): string {
  return filename.replace(/\.[^.]+$/, "").replace(/[[\]\r\n]/g, " ").trim();
}
